/**
 * Jumps the camera so that a given page's top-left corner aligns with the
 * viewport's content padding origin (top-align).
 */
import { useCallback, useMemo } from 'react';
import type { StoreApi } from 'zustand';
import type { Prettify } from '../../../lib/typeUtils';
import { coordinates, getCoordinateTransformContext } from '../../canvas';
import type { CameraSlice } from '../../canvas/core/camera';
import type { PanSlice } from '../../canvas/features/pan';
import {
  createSelectDocumentLayout,
  type PageLayoutSlice,
  useDocumentCanvasContext,
} from '../core';

export type ScrollToPageDeps = Prettify<
  CameraSlice & PageLayoutSlice & PanSlice
>;

export const useScrollToPage = (
  storeApi: StoreApi<ScrollToPageDeps>
): ((pageId: number) => void) => {
  const { getEffectiveDimensions } = useDocumentCanvasContext();
  const selectDocumentLayout = useMemo(
    () => createSelectDocumentLayout(getEffectiveDimensions),
    [getEffectiveDimensions]
  );

  return useCallback(
    (pageId: number) => {
      const state = storeApi.getState();
      const target = selectDocumentLayout(state).pageLayouts.find(
        l => l.pageId === pageId
      );
      if (!target) return;

      const ctx = getCoordinateTransformContext(state.canvas);
      const currentTopLeft = coordinates.point.contentToViewport(ctx)({
        x: target.x,
        y: target.y,
      });
      const desiredTopLeft = {
        x: ctx.contentPadding.left,
        y: ctx.contentPadding.top,
      };

      state.pan.actions.panBy({
        x: currentTopLeft.x - desiredTopLeft.x,
        y: currentTopLeft.y - desiredTopLeft.y,
      });
    },
    [storeApi, selectDocumentLayout]
  );
};
